import React from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import VisibilityIcon from "@mui/icons-material/Visibility";

import { GroupAdminInfo } from "../../api/groups";
import { Partner } from "../../api/partners";
import { useGroupsStore } from "../../store/groupsStore";

interface PartnerGroupsPanelProps {
  partner: Partner;
  onViewGroup: (group: GroupAdminInfo) => void;
}

// A partner owns a single backing group today; keeping this list-shaped lets
// multiple groups per partner slot in without reworking the panel.
const groupIdsOf = (partner: Partner): string[] => [partner.groupId];

const toGroupStub = (groupId: string, partner: Partner): GroupAdminInfo => ({
  id: groupId,
  name: partner.displayName,
  description: "",
  tag: partner.hashtag,
  members: [],
});

/** Expanded-row content in the partner list: the partner's groups. */
export const PartnerGroupsPanel: React.FC<PartnerGroupsPanelProps> = ({
  partner,
  onViewGroup,
}) => {
  const { groups, loadGroupDetail } = useGroupsStore();
  const groupIds = groupIdsOf(partner);

  React.useEffect(() => {
    groupIds
      .filter((id) => !groups.some((g) => g.id === id))
      .forEach((id) => {
        loadGroupDetail(id).catch(() => {
          /* name falls back to the partner's display name */
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner.id]);

  return (
    <Box sx={{ py: 1.5, pl: { xs: 1, sm: 6 } }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        Groups ({groupIds.length})
      </Typography>
      {groupIds.map((groupId) => {
        const group = groups.find((g) => g.id === groupId);
        return (
          <Box
            key={groupId}
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            {group ? (
              <Typography variant="body2">{group.name}</Typography>
            ) : (
              <CircularProgress size={12} />
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
            >
              {groupId}
            </Typography>
            <Tooltip title="View group">
              <IconButton
                size="small"
                onClick={() => onViewGroup(group ?? toGroupStub(groupId, partner))}
                sx={{ color: "#1976d2" }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
};
